import * as THREE from 'three';

const SHAPES = 6, cache: THREE.BufferGeometry[] = [];

/**
 * Weathered stones split along a few planes: broad flat faces and sharp arrises over a softly lumped core, with a flat base so
 * they sit on the ground rather than float: roughly unit-sized, bottom at y = -.5. Each variant has its own lumps, cleavage and
 * proportions, so a cluster never repeats the same stone. Geometries are shared.
 */
export function rockShape(index: number): THREE.BufferGeometry {
  const variant = ((index % SHAPES) + SHAPES) % SHAPES;
  if (cache[variant]) return cache[variant];
  const geometry = new THREE.IcosahedronGeometry(.62, 1), position = geometry.getAttribute('position');
  const a = 1.7 + variant * .73, b = 2.3 + variant * .41, c = .9 + variant * 1.13;
  const squash = [.82, .7, .95, .78, .88, .66][variant], lean = [0, .2, -.15, .1, -.22, .28][variant];
  for (let i = 0; i < position.count; i++) {
    const x = position.getX(i), y = position.getY(i), z = position.getZ(i);
    const lump = 1 + Math.sin(x * a + c) * .1 + Math.sin(y * b - c) * .09 + Math.sin(z * (a + b) * .6 + variant) * .08;
    position.setXYZ(i, x * lump + y * lean * .5, y * lump * squash + .05, z * lump * (variant % 3 === 0 ? .8 : 1));
  }
  cleave(position, cleavage(variant * 7 + 3, 6, .36, .46));
  for (let i = 0; i < position.count; i++) position.setY(i, Math.max(-.5, position.getY(i)));
  geometry.computeVertexNormals(); cache[variant] = geometry; return geometry;
}

let stoneGeometry: THREE.BufferGeometry | undefined;

/**
 * The scattered stone (pebbles, verge stones, outcrops, rubble): an irregular split stone centred on the origin, about 2 across
 * like the unit dodecahedron it replaces, so callers' sizes still read as radii.
 */
export function stoneShape(): THREE.BufferGeometry {
  if (stoneGeometry) return stoneGeometry;
  const geometry = new THREE.IcosahedronGeometry(1, 1), position = geometry.getAttribute('position');
  for (let i = 0; i < position.count; i++) {
    const x = position.getX(i), y = position.getY(i), z = position.getZ(i), lump = 1 + Math.sin(x * 2.3 + 1.1) * .08 + Math.sin(z * 3.1 - y * 1.7) * .07;
    position.setXYZ(i, x * lump * 1.08, y * lump * .8, z * lump * .94);
  }
  cleave(position, cleavage(41, 7, .66, .8));
  geometry.computeVertexNormals(); return stoneGeometry = geometry;
}

/** `count` planes, as [normal, distance], with seeded normals spread around the stone (rarely underneath) at distances in [near, far]. */
function cleavage(seed: number, count: number, near: number, far: number) {
  let state = seed;
  const random = () => { state = (Math.imul(state, 1664525) + 1013904223) >>> 0; return state / 4294967296; };
  return Array.from({ length: count }, (_, i) => {
    const azimuth = (i + random() * .7) / count * Math.PI * 2, rise = -.25 + random() * 1.1;
    return { normal: new THREE.Vector3(Math.cos(azimuth), rise, Math.sin(azimuth)).normalize(), distance: near + random() * (far - near) };
  });
}

/** Flattens everything beyond each plane onto it, so each plane leaves a broad flat face. */
function cleave(position: THREE.BufferAttribute | THREE.InterleavedBufferAttribute, planes: { normal: THREE.Vector3; distance: number }[]) {
  const point = new THREE.Vector3();
  for (let i = 0; i < position.count; i++) {
    point.fromBufferAttribute(position, i);
    for (const { normal, distance } of planes) { const beyond = point.dot(normal) - distance; if (beyond > 0) point.addScaledVector(normal, -beyond); }
    position.setXYZ(i, point.x, point.y, point.z);
  }
}
